/**
 * A Module providing helper methods to work with the SVG DOM.
 * @module utils/utilSvg
 */
export const svg = {

  /** {string} svg namespace */
  ns: 'http://www.w3.org/2000/svg',

  /** {SVGElement} */
  root: null,

  /**
   * Create a circle element.
   * @param cx center x-coordinate
   * @param cy center y-coordinate
   * @param r radius
   * @param {Object} [attrs] additional attributes to add
   * @return {SVGCircleElement}
   */
  createCircle (cx, cy, r, attrs) {
    const circle = this.create('circle');

    this.setAttributes(circle, Object.assign({
      cx: cx,
      cy: cy,
      r: r
    }, attrs || {}));

    return circle;
  },

  /**
   * Create a rectangle element.
   * @param x x-coordinate
   * @param y y-coordinate
   * @param width width
   * @param height height
   * @param {Object} [attrs] additional attributes to add
   * @return {SVGRectElement}
   */
  createRectangle (x, y, width, height, attrs) {
    const rect = this.create('rect');
    this.setAttributes(rect, Object.assign({
      x: x,
      y: y,
      width: width,
      height: height
    }, attrs || {}));

    return rect;
  },

  /**
   * Create a line element
   * @param x1 x-coordinate of line start
   * @param y1 y-coordinate of line start
   * @param x2 x-coordinate of line end
   * @param y2 y-coordinate of line end
   * @param {Object} [attrs] additional attributes to add
   * @return {SVGLineElement}
   */
  createLine (x1, y1, x2, y2, attrs) {
    const line = this.create('line');
    this.setAttributes(line, Object.assign({
      x1: x1,
      y1: y1,
      x2: x2,
      y2: y2
    }, attrs || {}));

    return line;
  },

  /**
   * Create a text element.
   * @param {Number} x x-coordinate of the base line
   * @param {Number} y y-coordinate of the base line
   * @param {String} text text
   * @param {Object} [attrs] additional attributes to add
   * @return {SVGTextElement}
   */
  createText (x, y, text, attrs) {
    const elText = svg.create('text');
    elText.innerHTML = String(text);
    this.setAttributes(elText, Object.assign({
      x: x,
      y: y
    }, attrs || {}));

    return elText;
  },

  /**
   * Create any namespaced SVG element.
   * @param {String} name
   * @param {Object} [attrs] additional attributes to add
   * @return {SVGElement}
   */
  create (name, attrs) {
    const el = document.createElementNS(this.ns, name);
    if (attrs) {
      this.setAttributes(el, attrs);
    }

    return el;
  },

  /**
   * Sets multiple attributes on an element.
   * @param {SVGElement} element The element
   * @param {Object} attributes Attributes that should get set
   * @private
   */
  setAttributes (element, attributes) {
    Object.keys(attributes).forEach((key) => {
      element.setAttribute(key, String(attributes[key]));
    });
  },

  /**
   * Move an SVG element by dx and dy.
   * @param {SVGElement} element
   * @param dx delta x
   * @param dy delta y
   */
  translate(element, dx, dy) {
    element.setAttribute('transform', 'translate(' + [dx, dy].join(',') + ')');
  }
};